/* assets/mcmarden-commerce.js — McMARDEN · NOIR · commerce layer.

   In the static build the bag was a localStorage array and the Uniform's
   20% was arithmetic in the browser. Neither survives contact with a real
   store: Shopify is authoritative on price, availability and totals, and
   anything the browser computes is a display claim the checkout may not
   honour.

   So this file does exactly three things:
     1. resolve a chosen size (and inseam) to a real variant id,
     2. POST it to /cart/add.js and hand the response to Dawn's own
        <cart-drawer> to render,
     3. keep the header count in step.

   It never prices anything. The Uniform saving shown in the buy block is
   rendered by Liquid from theme settings and is only true because a
   matching automatic discount exists in Admin — see the handoff notes.

   Depends on: mcmarden.js (toast), Dawn's cart-drawer.js, window.routes. */

(() => {
  "use strict";

  const catalog = () => (window.McMarden && window.McMarden.products) || [];
  const byId = (id) => catalog().find((p) => p.id === id);

  const cartAddUrl = () =>
    (window.routes && window.routes.cart_add_url) || "/cart/add";

  /* ── Variant resolution ────────────────────────────────────
     Shopify products carry ordered options; ours are Size, or Size +
     Inseam for the jeans. A variant is the pair. We match on the option
     values the swatches carry rather than on position, so re-ordering
     options in Admin can't silently mis-map a size to the wrong SKU. */
  function resolveVariant(product, choice) {
    if (!product || !product.variants || !product.variants.length) return null;
    const want1 = choice.size || null;
    const want2 = choice.inseam || null;

    let v = product.variants.find(
      (x) =>
        (want1 == null || String(x.o1) === String(want1)) &&
        (want2 == null || x.o2 == null || String(x.o2) === String(want2))
    );
    // A single-variant product (no size run) still has one variant; take it.
    if (!v && product.variants.length === 1) v = product.variants[0];
    return v || null;
  }

  /* ── Read the swatch selection out of a scope ──────────────────────
     Same rules as the static build: prefer an explicitly-marked group,
     fall back to the first swatch group, and never read a hidden inseam
     group — a leftover selection must not attach an inseam to a jacket. */
  function readChoice(scope) {
    const pressed = (sel) =>
      scope.querySelector(`${sel} .swatch[aria-pressed="true"]`)?.dataset.size;

    const size =
      pressed("[data-size-group]") ||
      scope.querySelector('.swatch[aria-pressed="true"]')?.dataset.size ||
      null;

    const inseamGroup = scope.querySelector("[data-inseam-group]");
    const inseamVisible = inseamGroup && !inseamGroup.closest("[hidden]");
    const inseam = inseamVisible
      ? inseamGroup.querySelector('.swatch[aria-pressed="true"]')?.dataset.size
      : null;

    return { size, inseam };
  }

  /* ── The one path into the bag ─────────────────────────────────
     Asks Shopify to re-render Dawn's own cart sections in the same
     round-trip, then hands them to <cart-drawer>. That is Dawn's
     documented refresh path — rebuilding the drawer ourselves would
     drift from it on every Dawn upgrade. */
  async function addItems(items, opener) {
    const drawer = document.querySelector("cart-drawer");
    const body = { items };

    if (drawer && typeof drawer.getSectionsToRender === "function") {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }

    let res;
    try {
      res = await fetch(cartAddUrl() + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body)
      });
    } catch (err) {
      toast("Couldn't reach the studio. Check your connection and try again.");
      return false;
    }

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      // Shopify returns a human-readable `description` for the common
      // failures (sold out, quantity unavailable). Prefer it over ours.
      toast((data && (data.description || data.message)) || "That piece couldn't be added.");
      return false;
    }

    if (drawer && data && data.sections) {
      drawer.renderContents(data);
      if (opener) drawer.setActiveElement?.(opener);
    } else {
      // No drawer on the page (or sections withheld) — fall back to the
      // cart page rather than silently doing nothing.
      window.location.href = (window.routes && window.routes.cart_url) || "/cart";
      return true;
    }

    refreshCount();
    return true;
  }

  /* Header count. Dawn re-renders its own bubble inside the drawer
     sections, but our header is custom, so it is updated from /cart.js.
     Hidden at zero rather than showing "(0)" — mirrors the `hidden`
     attribute the header sets on first render (see
     sections/mcmarden-header.liquid) so the badge disappears the moment
     the bag is emptied, not just on the next full page load. */
  async function refreshCount() {
    try {
      const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.querySelectorAll(".cart-count-bubble span:first-child").forEach((el) => {
        el.textContent = cart.item_count;
      });
    } catch { /* a stale count is not worth an error state */ }
  }

  /* ── Single piece ──────────────────────────────────────
     [data-add="<product-handle>"] on the tile, quick view and product page. */
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-add]");
    if (!btn) return;
    e.preventDefault();

    const product = byId(btn.dataset.add);
    if (!product) {
      toast("That piece isn't available right now.");
      return;
    }

    const scope = btn.closest("[data-product-scope]") || btn.closest(".qv") || document;
    const variant = resolveVariant(product, readChoice(scope));

    if (!variant) {
      toast("Choose a size first.");
      return;
    }
    if (variant.available === false) {
      toast(`${product.name} is sold out in that size.`);
      return;
    }

    btn.disabled = true;
    const ok = await addItems([{ id: variant.id, quantity: 1 }], btn);
    btn.disabled = false;
    if (ok) toast(`<b>${product.name}</b> added to the bag.`);
  });

  /* ── The Uniform ──────────────────────────────────────
     Adds every piece of the set in one request, so the bag either gets
     the whole look or none of it. The 20% is NOT applied here — it is an
     automatic discount in Admin, and Shopify applies it to the cart. If
     that discount is missing, the customer simply pays full price; they
     are never shown a total the checkout won't honour. */
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-uniform-add]");
    if (!btn) return;
    e.preventDefault();

    const scope = btn.closest("[data-uniform-buy]") || document;
    const set = (window.McMarden && window.McMarden.uniformSets || [])
      .find((s) => s.id === btn.dataset.uniformAdd);
    if (!set) return;

    const pick = (sel) => scope.querySelector(`${sel} .swatch[aria-pressed="true"]`)?.dataset.size;
    const top = pick("[data-size-group]");
    const waist = pick("[data-waist-group]");
    const inseam = pick("[data-inseam-group]");

    const items = [];
    for (const handle of set.pieces) {
      const product = byId(handle);
      if (!product) {
        toast("That set is incomplete right now.");
        return;
      }
      // The jeans take the waist and inseam; everything else takes the
      // lettered size shared across the tops.
      const isJeans = product.inseams && product.inseams.length;
      const variant = resolveVariant(product, {
        size: isJeans ? waist : top,
        inseam: isJeans ? inseam : null
      });
      if (!variant) {
        toast(`Choose a size for the ${product.name.toLowerCase()}.`);
        return;
      }
      if (variant.available === false) {
        toast(`${product.name} is sold out in that size — the set can't be completed.`);
        return;
      }
      items.push({ id: variant.id, quantity: 1 });
    }

    btn.disabled = true;
    const ok = await addItems(items, btn);
    btn.disabled = false;
    if (ok) toast(`<b>${set.name}</b> added to the bag.`);
  });

  /* ── Keep the real form field in step with the swatches ────────────
     The product page posts a genuine <form> so it works without JS. The
     swatches are buttons, so on their own they change nothing the cart
     can read — this mirrors the current selection into the hidden
     name="id" field. With JS off the <noscript> <select> carries the same
     name and does the same job.

     Runs on any swatch click anywhere, because the quick view and the
     tiles use the same markup and the same handler should serve all three. */
  function syncVariantField(scope) {
    const field = scope.querySelector("[data-variant-id]");
    if (!field) return;
    const handle = scope.querySelector("[data-add]")?.dataset.add;
    const product = handle ? byId(handle) : null;
    if (!product) return;
    const variant = resolveVariant(product, readChoice(scope));
    if (variant) field.value = variant.id;
  }

  document.addEventListener("click", (e) => {
    if (!e.target.closest(".swatch")) return;
    // The swatch's own aria-pressed is flipped by mcmarden.js on the same
    // click; defer so we read the settled state rather than the old one.
    setTimeout(() => {
      const scope = e.target.closest("[data-product-scope]") || e.target.closest(".qv");
      if (scope) syncVariantField(scope);
    }, 0);
  });

  /* Native submit — covers Enter in a field, where no button was clicked. */
  document.addEventListener("submit", async (e) => {
    const form = e.target.closest("form[action*='/cart/add']");
    if (!form) return;
    const scope = form.closest("[data-product-scope]") || document;
    const handle = form.querySelector("[data-add]")?.dataset.add;
    const product = handle ? byId(handle) : null;
    if (!product) return;                       // let Shopify handle it natively

    const variant = resolveVariant(product, readChoice(scope));
    if (!variant) return;                       // native POST is a safe fallback

    e.preventDefault();
    if (variant.available === false) {
      toast(`${product.name} is sold out in that size.`);
      return;
    }
    const ok = await addItems([{ id: variant.id, quantity: 1 }], form);
    if (ok) toast(`<b>${product.name}</b> added to the bag.`);
  });

  /* ── Open the drawer from our own header button ──────────────────
     Dawn binds its drawer to whatever carries id="cart-icon-bubble", and
     then getSectionsToRender() overwrites that element's innerHTML with
     Dawn's own bubble markup on every cart update. Putting that id on our
     button would therefore bind it correctly and then destroy it. So we
     bind by hand and keep the markup ours; the count is refreshed from
     /cart.js instead of by section replacement. */
  document.addEventListener("click", (e) => {
    const toggle = e.target.closest(".cart-toggle");
    if (!toggle) return;
    const drawer = document.querySelector("cart-drawer");
    if (!drawer) return;               // drawer disabled → let the link go to /cart
    e.preventDefault();
    drawer.open(toggle);
  });

  /* Keep the custom header bubble honest on first paint and on back/forward. */
  document.addEventListener("DOMContentLoaded", refreshCount);
  window.addEventListener("pageshow", (e) => { if (e.persisted) refreshCount(); });

  window.McMardenCart = { addItems, resolveVariant, refreshCount };
})();

/* ───────────────────────────────────────────────────────────────────────────────────────────────────────
   CART LINE: CHANGE SIZE IN PLACE
   The cart page renders each single-option line item as a <select>
   (see sections/main-cart-items.liquid). Shopify has no "change this
   line's variant" endpoint — a line item IS its variant — so a swap is
   an add of the new one plus a removal of the old.

   Order matters. Adding FIRST means a failure (the new size sold out
   between page load and the change, a quantity rule, a network drop)
   leaves the original line sitting in the cart untouched; removing
   first and then failing the add would silently empty the customer's
   cart. On failure the <select> is put back to the size it was on so
   the control never disagrees with the cart behind it.
   ─────────────────────────────────────────────────────────────────────────────────────────────────────── */

(() => {
  const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" };

  document.addEventListener("change", async (e) => {
    const select = e.target.closest("[data-cart-variant-select]");
    if (!select) return;

    const newVariantId = select.value;
    const lineKey = select.dataset.lineKey;
    const quantity = parseInt(select.dataset.quantity, 10) || 1;
    // Remembered before the request so the control can be restored if
    // anything below fails.
    const previous = select.dataset.previousValue || select.querySelector("option[selected]")?.value;

    select.disabled = true;
    try {
      const added = await fetch("/cart/add.js", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ items: [{ id: newVariantId, quantity: quantity }] })
      });
      if (!added.ok) throw new Error("add failed");

      const removed = await fetch("/cart/change.js", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
      if (!removed.ok) throw new Error("remove failed");

      window.location.reload();
    } catch (err) {
      select.disabled = false;
      if (previous) select.value = previous;
      // toast() is a global declared in mcmarden.js, called bare
      // elsewhere in this file; guarded here only so a failed load of
      // that file can't turn a recoverable error into a ReferenceError.
      if (typeof toast === "function") {
        toast("That size couldn't be updated. Please try again.");
      }
    }
  });

  /* Track the last good value so a failed swap can be reverted. */
  document.addEventListener("focusin", (e) => {
    const select = e.target.closest("[data-cart-variant-select]");
    if (select) select.dataset.previousValue = select.value;
  });
})();

/* ───────────────────────────────────────────────────────────────────────────────────────────────────────
   CART: PROMO CODE
   /discount/CODE?redirect=/cart is the only storefront-supported way to
   apply a discount — there is no cart AJAX endpoint for codes. Enter
   submits as well as the button, because a code field that ignores the
   return key reads as broken.
   ─────────────────────────────────────────────────────────────────────────────────────────────────────── */

(() => {
  const apply = (input) => {
    const code = (input.value || "").trim();
    if (!code) { input.focus(); return; }
    const back = (window.routes && window.routes.cart_url) || "/cart";
    window.location.href =
      "/discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(back);
  };

  document.addEventListener("click", (e) => {
    if (!e.target.closest("[data-discount-apply]")) return;
    const wrap = e.target.closest(".cart__discount");
    const input = wrap && wrap.querySelector("[data-discount-input]");
    if (input) apply(input);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    const input = e.target.closest("[data-discount-input]");
    if (!input) return;
    // The field sits inside the cart <form>, so Enter would otherwise
    // submit the cart to checkout instead of applying the code.
    e.preventDefault();
    apply(input);
  });
})();
